import { get, post, postFileUpload } from "../api.services";
import { BackgroundProcessEntry } from "@/services/backgroundProcess";
import { Container } from "@/services/fermenterRuns/fermenterRunsService";
import { PlateLayout } from "../commonModels";

/**
 * Common parameters between template and experiment setup
 */
type CommonParams = {
    useOrderedPeptides: boolean;
    orderIds: string[];
    experimentName: string;
    plateLayout: PlateLayout;
};

/**
 * Parameters for fermenter run setup
 */
export type FermenterRunContextParams = CommonParams & {
    protocolCode: string;
    experimentGroupId: string;
    fermenterId: string;
    groupByFermenterSeed: boolean;
};

export type FermenterRunTemplateParams = FermenterRunContextParams & {
    numberOfFermenterBatchesPerPeptideBatch: number;
    numberOfPeptideBatchesPerPeptide: number;
};

export type SetupFermenterRunExperimentParams = FermenterRunContextParams & {
    metadata: Record<string, any>;
    folderId: string;
    startDate: string;
};

/**
 * @deprecated remove after we migrate to new Fermenter Run flow
 */
export type LegacyFermenterRunTemplateParams = {
    useOrderedPeptides: boolean;
    plateLayout: PlateLayout;
    experimentName: string;
    protocolCode: string;
    fermenterId: string;
    peptideBatchCodes: string[];
    numberOfBatchesPerPeptide: number;
};

export type FermenterRunSetupErrors = {
    skippedAttributes: string[];
    skippedRows: string[];
};

export type ValidFermenterRunSetup = FermenterRunSetupErrors & {
    experimentCode: string;
};

/**
 * @deprecated remove after we migrate to new Fermenter Run flow
 */
export type LegacySetupFermenterRunExperimentParams = Omit<
    SetupFermenterRunExperimentParams,
    | "numberOfPeptideBatchesPerPeptide"
    | "numberOfFermenterBatchesPerPeptideBatch"
    | "groupByFermenterSeed"
    | "orderIds"
>;

export async function downloadV2FermenterRunTemplate(payload: FermenterRunTemplateParams) {
    return post(`/fermenterRuns/downloadV2FermenterRunTemplate`, payload, {
        responseType: "blob",
    });
}

export async function setupFermenterRunExperiment(
    formData: FormData
): Promise<{ backgroundProcessId: string }> {
    return post(`/fermenterRuns/setupV2FermenterRunExperiment`, formData);
}

/**
 * @deprecated remove after we migrate to new Fermenter Run flow
 */
export async function downloadLegacyFermenterRunTemplate(
    payload: LegacyFermenterRunTemplateParams
) {
    return post(`/fermenterRuns/downloadLegacyFermenterRunTemplate`, payload, {
        responseType: "blob",
    });
}

/**
 * @deprecated remove after we migrate to new Fermenter Run flow
 */
export async function setupLegacyFermenterRunExperiment(
    payload: LegacySetupFermenterRunExperimentParams
): Promise<{ experimentCode: string }> {
    return postFileUpload(`/fermenterRuns/setupLegacyFermenterRunExperiment`, payload);
}

export async function fetchSetupFermenterRunExperimentStatus(
    backgroundProcessId: string
): Promise<BackgroundProcessEntry<ValidFermenterRunSetup>> {
    return get(`/fermenterRuns/setupFermenterRunExperiment/status/${backgroundProcessId}`);
}

export async function fetchBatchesForFermenterRun(experimentCode: string, batchType: string) {
    return get(`/fermenterRuns/batches/${experimentCode}/${batchType}`);
}

export async function fetchContainers(experimentCode: string): Promise<Container[]> {
    return await get(`/fermenterRuns/${experimentCode}/containers`);
}

export async function downloadPlateMap(experimentCode: string) {
    return get(`/fermenterRuns/${experimentCode}/plateMap`, {
        responseType: "blob",
    });
}
